import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  Facebook, MessageCircle, RefreshCw, Loader2, Search, X, Settings2,
  CheckCircle, Phone, Mail, Megaphone, Plus, Trash2, Pencil, Link2, Download,
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';

const DB_LISTS = ['Universal Performing Arts', 'Primas', 'Adult Street Dance', 'Bootcamps'];

const STATUS_STYLES = {
  new: 'bg-[#FF007F] text-white',
  messaged: 'bg-emerald-600 text-white',
  booked: 'bg-[#7000FF] text-white',
  closed: 'bg-gray-600 text-white',
};

const renderTemplate = (text, lead) => {
  const map = {
    name: lead.name, parent_name: lead.name, first_name: lead.first_name || lead.name,
    child_name: lead.child_name || 'your child', email: lead.email, phone: lead.phone,
    campaign: lead.campaign_name, form: lead.form_name, ad: lead.ad_name,
    ...(lead.answers || {}),
  };
  return text.replace(/\{([a-z_']+)\}/gi, (m, key) => {
    const v = map[key] ?? map[key.toLowerCase()];
    return v !== undefined && v !== null && v !== '' ? String(v) : m === '{child_name}' ? 'your child' : '';
  });
};

const pickTemplate = (templates, lead) => {
  const haystack = `${lead.form_name || ''} ${lead.campaign_name || ''} ${lead.ad_name || ''} ${lead.adset_name || ''} ${lead.db_list || ''}`.toLowerCase();
  const match = templates.find(t => !t.is_default && (t.match_keyword || '')
    .split(/[,·;|/]+/)
    .map(k => k.trim().toLowerCase())
    .filter(Boolean)
    .some(k => haystack.includes(k)));
  return match || templates.find(t => t.is_default) || templates[0];
};

const FbLeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [metaStatus, setMetaStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [listFilter, setListFilter] = useState('');
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [editTpl, setEditTpl] = useState(null);
  const [tplForm, setTplForm] = useState({ name: '', match_keyword: '', message: '' });
  const [savingTpl, setSavingTpl] = useState(false);

  const load = useCallback(async () => {
    try {
      const [l, t, s] = await Promise.all([
        api.get('/fb-leads'),
        api.get('/fb-leads/templates'),
        api.get('/fb-leads/meta-status'),
      ]);
      setLeads(l.data);
      setTemplates(t.data);
      setMetaStatus(s.data);
    } catch (e) {
      toast.error('Failed to load Facebook leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/fb-leads/sync');
      const extra = r.data.auto_booked ? ` · ${r.data.auto_booked} matched to trial bookings` : '';
      toast.success(`Sync complete — ${r.data.imported} new enquir${r.data.imported === 1 ? 'y' : 'ies'} imported${extra}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  const setLeadStatus = async (lead, status) => {
    setLeads(prev => prev.map(l => (l.lead_id === lead.lead_id ? { ...l, status } : l)));
    try {
      await api.patch(`/fb-leads/${lead.lead_id}`, { status });
    } catch (e) {
      toast.error('Failed to update status');
      load();
    }
  };

  const setLeadList = async (lead, dbList) => {
    setLeads(prev => prev.map(l => (l.lead_id === lead.lead_id ? { ...l, db_list: dbList || null } : l)));
    try {
      await api.patch(`/fb-leads/${lead.lead_id}`, { db_list: dbList });
      if (dbList) {
        toast.success(lead.status !== 'new' && !listFilter
          ? `Added to ${dbList} — moved out of the Inbox`
          : `Added to ${dbList} list`);
      }
    } catch (e) {
      toast.error('Failed to update list');
      load();
    }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (listFilter && listFilter !== 'ALL') params.set('db_list', listFilter);
      const r = await api.get(`/fb-leads/export?${params.toString()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `upa-database-${((listFilter !== 'ALL' && listFilter) || statusFilter || 'all').toLowerCase().replace(/\s+/g, '-')}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const sendWhatsApp = (lead) => {
    if (!lead.phone) { toast.error('This enquiry has no phone number'); return; }
    const tpl = pickTemplate(templates, lead);
    if (!tpl) { toast.error('No WhatsApp template set up yet'); return; }
    const msg = renderTemplate(tpl.message, lead);
    const digits = lead.phone.replace(/[^\d]/g, '');
    window.open(`https://wa.me/${digits}?text=${encodeURIComponent(msg)}`, '_blank');
    if (lead.status === 'new') {
      setLeadStatus(lead, 'messaged');
      if (lead.db_list && !listFilter) toast.success(`Moved to the ${lead.db_list} list`);
    }
  };

  const openTplEditor = (tpl) => {
    setEditTpl(tpl || null);
    setTplForm(tpl ? { name: tpl.name, match_keyword: tpl.match_keyword || '', message: tpl.message } : { name: '', match_keyword: '', message: '' });
  };

  const saveTemplate = async () => {
    if (!tplForm.name.trim() || !tplForm.message.trim()) { toast.error('Name and message are required'); return; }
    setSavingTpl(true);
    try {
      if (editTpl) await api.put(`/fb-leads/templates/${editTpl.id}`, tplForm);
      else await api.post('/fb-leads/templates', tplForm);
      toast.success('Template saved');
      setEditTpl(null);
      setTplForm({ name: '', match_keyword: '', message: '' });
      const t = await api.get('/fb-leads/templates');
      setTemplates(t.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSavingTpl(false);
    }
  };

  const deleteTemplate = async (tpl) => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try {
      await api.delete(`/fb-leads/templates/${tpl.id}`);
      setTemplates(prev => prev.filter(t => t.id !== tpl.id));
      toast.success('Template deleted');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  const inInbox = (l) => !(l.db_list && l.status !== 'new');

  const filtered = leads.filter(l => {
    const matchesStatus = statusFilter === 'all' || l.status === statusFilter;
    const matchesList = listFilter === 'ALL' ? true : listFilter ? l.db_list === listFilter : inInbox(l);
    const hay = `${l.name || ''} ${l.email || ''} ${l.phone || ''} ${l.campaign_name || ''} ${l.form_name || ''}`.toLowerCase();
    return matchesStatus && matchesList && (!search || hay.includes(search.toLowerCase()));
  });

  const counts = leads.reduce((a, l) => { a[l.status] = (a[l.status] || 0) + 1; return a; }, {});
  const listCounts = leads.reduce((a, l) => { if (l.db_list) a[l.db_list] = (a[l.db_list] || 0) + 1; return a; }, {});
  const inboxCount = leads.filter(inInbox).length;

  return (
    <div className="space-y-6" data-testid="fb-leads-tab">
      {/* Connection status */}
      <Card className="bg-[#111] border-[#333]">
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 bg-[#1877F2] rounded-lg flex items-center justify-center shrink-0">
              <Facebook className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1 min-w-[200px]">
              <p className="text-white font-medium">Facebook Lead Ads</p>
              {metaStatus?.configured ? (
                metaStatus.token_ok ? (
                  <p className="text-sm text-emerald-400 flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" />Connected{metaStatus.page_name ? ` — ${metaStatus.page_name}` : ''}</p>
                ) : (
                  <p className="text-sm text-red-400">Connected but token invalid — reconnect your Meta token</p>
                )
              ) : (
                <p className="text-sm text-amber-400">Not connected yet — add your Meta keys, then follow setup</p>
              )}
            </div>
            <Button variant="outline" size="sm" onClick={() => setSetupOpen(!setupOpen)} className="border-[#333] text-white" data-testid="fb-setup-toggle">
              <Settings2 className="w-4 h-4 mr-1" />Setup
            </Button>
            <Button size="sm" onClick={syncNow} disabled={syncing || !metaStatus?.configured} className="bg-[#1877F2] hover:bg-[#1465d8] text-white" data-testid="fb-sync-button">
              {syncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}Sync now
            </Button>
            <Button size="sm" variant="outline" onClick={() => setTemplatesOpen(true)} className="border-[#333] text-white" data-testid="fb-templates-button">
              <MessageCircle className="w-4 h-4 mr-1" />Templates
            </Button>
          </div>
          {setupOpen && metaStatus && (
            <div className="mt-4 p-4 bg-[#0a0a0a] rounded-lg border border-[#333] text-sm text-white/70 space-y-2" data-testid="fb-setup-panel">
              <p className="text-white font-medium">Meta webhook setup (one-off)</p>
              <p>1. In your Meta app dashboard → Webhooks → Page, set the Callback URL and Verify Token below, and subscribe to <code className="text-[#FF007F]">leadgen</code>.</p>
              <p className="flex items-center gap-2 break-all"><Link2 className="w-4 h-4 shrink-0" /><code className="text-emerald-400">{metaStatus.webhook_url}</code></p>
              <p className="break-all">Verify token: <code className="text-emerald-400">{metaStatus.verify_token || 'not set'}</code></p>
              <p>2. Subscribe your Page: POST /&#123;page-id&#125;/subscribed_apps with subscribed_fields=leadgen (or press Sync now to pull existing leads anytime).</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {['all', 'new', 'messaged', 'booked', 'closed'].map(s => (
          <Button key={s} size="sm" variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
            className={statusFilter === s ? 'bg-[#FF007F] hover:bg-[#d60068] text-white' : 'border-[#333] text-white'}
            data-testid={`fb-filter-${s}`}>
            {s === 'all' ? `All (${leads.length})` : `${s.charAt(0).toUpperCase() + s.slice(1)} (${counts[s] || 0})`}
          </Button>
        ))}
        <div className="relative flex-1 min-w-[180px] max-w-xs ml-auto">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search enquiries..."
            className="pl-9 bg-[#111] border-[#333] text-white" data-testid="fb-search-input" />
          {search && <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40"><X className="w-4 h-4" /></button>}
        </div>
      </div>

      {/* Database lists */}
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-white/40 text-xs uppercase tracking-wider mr-1">Database:</span>
        <Button size="sm" variant={!listFilter ? 'default' : 'outline'} onClick={() => setListFilter('')}
          className={!listFilter ? 'bg-[#7000FF] hover:bg-[#5c00d6] text-white' : 'border-[#333] text-white'}
          data-testid="fb-list-filter-all">
          Inbox ({inboxCount})
        </Button>
        {DB_LISTS.map(name => (
          <Button key={name} size="sm" variant={listFilter === name ? 'default' : 'outline'}
            onClick={() => setListFilter(listFilter === name ? '' : name)}
            className={listFilter === name ? 'bg-[#7000FF] hover:bg-[#5c00d6] text-white' : 'border-[#333] text-white'}
            data-testid={`fb-list-filter-${name.toLowerCase().replace(/\s+/g, '-')}`}>
            {name} ({listCounts[name] || 0})
          </Button>
        ))}
        <Button size="sm" variant={listFilter === 'ALL' ? 'default' : 'outline'} onClick={() => setListFilter('ALL')}
          className={listFilter === 'ALL' ? 'bg-[#7000FF] hover:bg-[#5c00d6] text-white' : 'border-[#333] text-white'}
          data-testid="fb-list-filter-everyone">
          All ({leads.length})
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv} disabled={exporting || filtered.length === 0}
          className="border-[#333] text-white ml-auto" data-testid="fb-export-csv-button">
          {exporting ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Export CSV
        </Button>
      </div>

      {/* Leads list */}
      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <div key={i} className="h-24 bg-[#111] rounded-xl animate-pulse" />)}</div>
      ) : filtered.length === 0 ? (
        <Card className="bg-[#111] border-[#333]"><CardContent className="py-12 text-center text-white/50">
          {leads.length === 0 ? 'No Facebook enquiries yet. Once connected, new leads appear here automatically — or press Sync now.' : 'No enquiries match your filter.'}
        </CardContent></Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(lead => {
            const tpl = pickTemplate(templates, lead);
            return (
              <Card key={lead.lead_id} className="bg-[#111] border-[#333]" data-testid={`fb-lead-${lead.lead_id}`}>
                <CardContent className="pt-5 pb-4">
                  <div className="flex flex-wrap items-start gap-3">
                    <div className="flex-1 min-w-[220px]">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-white font-semibold">{lead.name || 'Unknown name'}</p>
                        <Badge className={STATUS_STYLES[lead.status] || 'bg-gray-600 text-white'}>{lead.status}{lead.status === 'booked' && lead.booked_via === 'trial' ? ' · via trial' : ''}</Badge>
                        {lead.db_list && <Badge className="bg-[#7000FF]/20 text-[#b57aff] border border-[#7000FF]/40">{lead.db_list}</Badge>}
                        {lead.platform && <Badge variant="outline" className="border-[#333] text-white/60">{lead.platform}</Badge>}
                      </div>
                      <div className="mt-1 text-sm text-white/60 space-y-0.5">
                        {lead.phone && <p className="flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" />{lead.phone}</p>}
                        {lead.email && <p className="flex items-center gap-1.5 break-all"><Mail className="w-3.5 h-3.5" />{lead.email}</p>}
                        {(lead.form_name || lead.campaign_name || lead.ad_name) && (
                          <p className="flex items-center gap-1.5 text-[#FF007F]"><Megaphone className="w-3.5 h-3.5" />
                            {[lead.campaign_name, lead.form_name || lead.ad_name].filter(Boolean).join(' · ')}
                          </p>
                        )}
                        {lead.created_time && <p className="text-white/40 text-xs">{new Date(lead.created_time).toLocaleString('en-GB')}</p>}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-2">
                      <Button size="sm" onClick={() => sendWhatsApp(lead)} disabled={!lead.phone}
                        className="bg-[#25D366] hover:bg-[#1fb356] text-white font-semibold"
                        data-testid={`fb-whatsapp-${lead.lead_id}`}>
                        <MessageCircle className="w-4 h-4 mr-1.5" />Send WhatsApp
                      </Button>
                      {tpl && <p className="text-[11px] text-white/40">template: {tpl.name}</p>}
                      <select value={lead.status} onChange={e => setLeadStatus(lead, e.target.value)}
                        className="bg-[#0a0a0a] border border-[#333] text-white/80 text-xs rounded-md px-2 py-1"
                        data-testid={`fb-status-select-${lead.lead_id}`}>
                        <option value="new">New</option>
                        <option value="messaged">Messaged</option>
                        <option value="booked">Booked</option>
                        <option value="closed">Closed</option>
                      </select>
                      <select value={lead.db_list || ''} onChange={e => setLeadList(lead, e.target.value)}
                        className="bg-[#0a0a0a] border border-[#333] text-white/80 text-xs rounded-md px-2 py-1"
                        data-testid={`fb-list-select-${lead.lead_id}`}>
                        <option value="">No list</option>
                        {DB_LISTS.map(n => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Templates dialog */}
      <Dialog open={templatesOpen} onOpenChange={setTemplatesOpen}>
        <DialogContent className="bg-[#111] border-[#333] text-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>WhatsApp Templates</DialogTitle></DialogHeader>
          <p className="text-sm text-white/60">
            Each enquiry picks the first template with a keyword that appears in its campaign, form or ad name, or its database list — otherwise the default is used.
            Separate multiple keywords with commas (e.g. <code className="text-[#FF007F]">adult, street dance</code>) — any one of them matching is enough.
            Placeholders: <code className="text-[#FF007F]">{'{first_name} {name} {child_name} {campaign} {form}'}</code> plus any form answer key.
          </p>
          <div className="space-y-3">
            {templates.map(tpl => (
              <div key={tpl.id} className="p-3 bg-[#0a0a0a] rounded-lg border border-[#333]" data-testid={`fb-template-${tpl.id}`}>
                <div className="flex items-center gap-2">
                  <p className="font-medium flex-1">{tpl.name}{tpl.is_default && <Badge className="ml-2 bg-[#7000FF] text-white">default</Badge>}</p>
                  {tpl.match_keyword && <Badge variant="outline" className="border-[#333] text-white/60">keyword: {tpl.match_keyword}</Badge>}
                  <Button size="icon" variant="ghost" onClick={() => openTplEditor(tpl)} data-testid={`fb-template-edit-${tpl.id}`}><Pencil className="w-4 h-4" /></Button>
                  {!tpl.is_default && <Button size="icon" variant="ghost" className="text-red-400" onClick={() => deleteTemplate(tpl)} data-testid={`fb-template-delete-${tpl.id}`}><Trash2 className="w-4 h-4" /></Button>}
                </div>
                <p className="text-sm text-white/50 mt-1 whitespace-pre-wrap">{tpl.message}</p>
              </div>
            ))}
          </div>
          <div className="p-3 bg-[#0a0a0a] rounded-lg border border-dashed border-[#333] space-y-3">
            <p className="font-medium">{editTpl ? `Edit: ${editTpl.name}` : 'Add a funnel template'}</p>
            <Input placeholder="Template name (e.g. Primatotterinas funnel)" value={tplForm.name}
              onChange={e => setTplForm(f => ({ ...f, name: e.target.value }))}
              className="bg-[#111] border-[#333] text-white" data-testid="fb-template-name-input" />
            {!editTpl?.is_default && (
              <Input placeholder="Match keyword (e.g. primatotterinas, bootcamp) — leave blank for default only" value={tplForm.match_keyword}
                onChange={e => setTplForm(f => ({ ...f, match_keyword: e.target.value }))}
                className="bg-[#111] border-[#333] text-white" data-testid="fb-template-keyword-input" />
            )}
            <Textarea rows={5} placeholder="Hi {first_name}! Thanks for your enquiry about..." value={tplForm.message}
              onChange={e => setTplForm(f => ({ ...f, message: e.target.value }))}
              className="bg-[#111] border-[#333] text-white" data-testid="fb-template-message-input" />
            <div className="flex gap-2">
              <Button onClick={saveTemplate} disabled={savingTpl} className="bg-[#FF007F] hover:bg-[#d60068] text-white" data-testid="fb-template-save-button">
                {savingTpl ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Plus className="w-4 h-4 mr-1" />}{editTpl ? 'Save changes' : 'Add template'}
              </Button>
              {editTpl && <Button variant="outline" className="border-[#333] text-white" onClick={() => openTplEditor(null)}>Cancel edit</Button>}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-[#333] text-white" onClick={() => setTemplatesOpen(false)}>Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FbLeadsTab;
